import React, { useEffect, useState } from "react";
import { Building2, Shield, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { CtaBanner } from "@/pages/Home";
import { api } from "@/lib/api";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    api.getPublicPartners()
      .then((d) => setPartners(d.partners || []))
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEOHead
        title="Partner Carriers — Hylas Companion"
        description="Verified fleet partners of Hylas Companion. These carriers have signed the Fleet Client Agreement and are actively hiring CDL-A drivers through the Hylas Market platform."
        path="/partners"
      />

      {/* ── Hero ── */}
      <section className="relative bg-[#0A0A0A] pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(255,77,0,0.08) 0%, transparent 70%)" }} />
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="font-head text-[10px] uppercase tracking-[0.4em] mb-4"
            style={{ color: ACCENT }}>Hylas Companion</p>
          <h1 className="font-head text-5xl sm:text-6xl font-black uppercase tracking-tight text-white leading-none mb-6">
            Partner Carriers
          </h1>
          <p className="text-white/50 text-lg max-w-2xl mx-auto leading-relaxed mb-10">
            Every carrier listed here has signed the{" "}
            <span className="text-white font-medium">Fleet Client Agreement</span>{" "}
            with Andrej Sasaroga DBA Hylas Companion and is actively hiring CDL-A drivers through the Hylas Market platform.
          </p>
          <div className="inline-flex items-center gap-6 px-6 py-3 text-sm"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div className="flex items-center gap-2 text-white/50">
              <Shield className="w-4 h-4" style={{ color: ACCENT }} />
              <span>Fleet Client Agreement signed</span>
            </div>
            <div className="w-px h-4 bg-white/10" />
            <div className="flex items-center gap-2 text-white/50">
              <CheckCircle2 className="w-4 h-4" style={{ color: GOLD }} />
              <span>Verified by Hylas</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Disclaimer ── */}
      <section className="bg-[#0D0D0D] border-b border-white/[0.05] py-6">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-white/30 text-xs text-center leading-relaxed max-w-3xl mx-auto">
            <strong className="text-white/50">Important:</strong>{" "}
            Andrej Sasaroga DBA Hylas Companion is a driver recruiting and marketing service only — NOT a motor carrier, employer, or consumer reporting agency.
            Each partner carrier listed below is an independent employer. Upon referral, each carrier will independently conduct FMCSA-required background, MVR, PSP, and Clearinghouse checks under their own FCRA authorization.
          </p>
        </div>
      </section>

      {/* ── Partner Grid ── */}
      <section className="bg-[#0A0A0A] py-16 min-h-[40vh]">
        <div className="max-w-5xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center gap-3 py-20 text-white/30">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="font-head text-xs uppercase tracking-[0.2em]">Loading partners…</span>
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="w-10 h-10 mx-auto mb-4 text-white/10" />
              <p className="font-head text-xs uppercase tracking-[0.2em] text-white/25">No partners listed yet</p>
              <p className="text-white/20 text-sm mt-2">Carriers will appear here once they sign the Fleet Client Agreement.</p>
            </div>
          ) : (
            <>
              <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-8"
                style={{ color: "rgba(255,255,255,0.2)" }}>
                {partners.length} Verified Partner{partners.length !== 1 ? "s" : ""}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {partners.map((p, i) => (
                  <PartnerCard key={i} company={p.company} since={p.since} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── Become a Partner ── */}
      <section className="bg-[#0D0D0D] py-16" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-head text-[9px] uppercase tracking-[0.35em] mb-4" style={{ color: ACCENT }}>
            For Carriers
          </p>
          <h2 className="font-head text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mb-4">
            Become a Partner
          </h2>
          <p className="text-white/40 text-base mb-8 leading-relaxed">
            Access pre-screened CDL-A drivers through the Hylas Market platform.
            Sign the Fleet Client Agreement and start receiving qualified driver referrals within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/fleet"
              className="inline-flex items-center justify-center gap-2 font-head text-sm font-black uppercase tracking-[0.15em] px-6 py-3 transition-all hover:brightness-110"
              style={{ background: ACCENT, color: "#fff" }}>
              Learn More <ArrowRight className="w-4 h-4" />
            </a>
            <a href="/market"
              className="inline-flex items-center justify-center gap-2 font-head text-sm uppercase tracking-[0.15em] px-6 py-3 transition-colors hover:text-white"
              style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}>
              Open Hylas Market
            </a>
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}

function PartnerCard({ company, since }) {
  const initials = company
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  const fmtDate = (s) => {
    if (!s) return null;
    try {
      return new Date(s).toLocaleDateString("en-US", { month: "short", year: "numeric" });
    } catch { return null; }
  };

  return (
    <div className="group flex items-start gap-4 p-5 transition-all"
      style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,77,0,0.25)"; e.currentTarget.style.background = "rgba(255,77,0,0.03)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}>
      {/* Initials avatar */}
      <div className="w-10 h-10 shrink-0 flex items-center justify-center font-head font-black text-xs"
        style={{ background: "rgba(255,77,0,0.1)", border: "1px solid rgba(255,77,0,0.2)", color: ACCENT }}>
        {initials || <Building2 className="w-4 h-4" />}
      </div>
      <div className="min-w-0">
        <p className="font-head font-black text-white text-sm leading-tight truncate">{company}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <CheckCircle2 className="w-3 h-3 shrink-0" style={{ color: GOLD }} />
          <span className="font-head text-[9px] uppercase tracking-[0.15em] text-white/30">
            Agreement Signed{since && fmtDate(since) ? ` · ${fmtDate(since)}` : ""}
          </span>
        </div>
      </div>
    </div>
  );
}
